import { type DpPriceReviewItem } from '@/infrastructure/CompetitorV2Client';

export const SOURCE_HOSTS: Record<string, string> = {
  stylekorean: 'stylekorean.com',
  yesstyle: 'yesstyle.com',
  stylevana: 'stylevana.com',
  oliveyoung_global: 'global.oliveyoung.com',
  amazon: 'www.amazon.com',
};
// Hold codes written by SkinSeoulBatch CalculateCompetitorDpPricingHandler (and its input builder and calculator);
// a PROPOSED row stores its review reason (CompetitorPricingCalculator reviewReason) in the same column.
export const HOLD_REASONS: Record<string, string> = {
  large_change_review: 'The price changes by 30% or more',
  top_seller_increase_review: 'Price increase on a top-selling product',
  margin_or_competitor_price: 'The new price is below cost',
  official_store_increase_review: 'Price increase above the competitor price, based on the official store cost',
  unsupported_price_state: 'Current sale and list price structure is unsupported',
  target_unavailable: 'Product or current price unavailable',
  FX_UNAVAILABLE: 'Exchange rate unavailable',
  PRICING_CONFIG_UNAVAILABLE: 'Pricing configuration unavailable',
  PRICING_INPUT_PROJECTION_UNAVAILABLE: 'Pricing input unavailable',
  EXCLUSIVITY_AUTHORITY_UNAVAILABLE: 'Exclusivity data unavailable',
  CAMPAIGN_AUTHORITY_UNAVAILABLE: 'Campaign data unavailable',
  MSRP_AUTHORITY_UNAVAILABLE: 'MSRP data unavailable',
  OVERSTOCK_AUTHORITY_UNAVAILABLE: 'Overstock data unavailable',
  ORDER_AUTHORITY_UNAVAILABLE: 'Order cost data unavailable',
  TARGET_QUANTITY_UNAVAILABLE: 'Product quantity unavailable',
  NO_ELIGIBLE_COMPETITOR: 'No eligible competitor price',
  COMPETITOR_QUANTITY_UNVERIFIED: 'Competitor quantity unverified',
  COST_PACK_BASIS_UNAVAILABLE: 'Cost pack basis unavailable',
  MSRP_DUPLICATE_CONFLICT: 'Conflicting MSRP entries',
  INPUT_INVALID: 'Pricing input invalid',
  POLICY_INPUT_UNAVAILABLE: 'Pricing policy unavailable',
  OVERSTOCK_EXCLUDED: 'Excluded as overstock',
  MSRP_INCREASE_CAP_UNCONFIRMED: 'MSRP increase cap unconfirmed',
  COST_INPUT_INVALID: 'Cost input invalid',
  COST_UNAVAILABLE: 'Cost unavailable',
  COMPETITOR_UNAVAILABLE: 'Competitor price unavailable',
  COMPETITOR_SELECTION_UNVERIFIED: 'Competitor selection unverified',
  COMPETITOR_PRICE_INVALID: 'Competitor price invalid',
  WEIGHT_INPUT_UNAVAILABLE: 'Weight unavailable',
  WEIGHT_INPUT_INVALID: 'Weight invalid',
  PRICE_OUTPUT_INVALID: 'Calculated price invalid',
  // CompetitorPricingCalculator reasons; Batch holds a row with the first one when it cannot price it.
  COGS_CORRUPT_FIXED_PRICE: 'Cost looks wrong, so the price stays fixed',
  FIXED_BRAND_EXCLUDED: 'Price fixed: brand excluded',
  FIXED_BUNDLE: 'Price fixed: bundle',
  FIXED_SKU_EXCLUDED: 'Price fixed: product excluded',
  MARGIN_CAP: 'Price limited by the margin cap',
  SAFETY_PRICE_CORRECTION: 'Price corrected by the safety range',
  CAMPAIGN_MINIMUM_DISCOUNT: 'Campaign minimum discount applied',
  PRICE_INCREASE_CAPPED: 'Price increase capped',
  FINAL_PAYABLE_FLOOR_RESTORED: 'Price raised back to the payable floor',
  PRICE_INCREASE_CAP_BELOW_FLOOR: 'Price increase cap is below the floor',
  CAMPAIGN_REGULAR_PRICE_ADJUSTED: 'Regular price adjusted for a campaign',
};

// Codes from Facade internal-api DecideDpPriceReviewHandler; each is raised before any store write.
// Auto publish (PublishDpPriceReviewsHandler) also stores them as the hold reason of a PROPOSED row it could not publish.
export const DP_PRICE_ERRORS: Record<string, string> = {
  DP_PRICE_BELOW_COST: 'The price is below the current cost. Check the amount.',
  DP_PRICE_MATCH_NOT_APPROVED: 'The competitor match for this price is no longer approved. Review the match first.',
  DP_PRICE_STATE_UNSUPPORTED: "The product's current sale and regular price structure is unsupported.",
  DP_PRICE_REVIEW_CLOSED: 'This price review was already closed. Reload the queue.',
  DP_PRICE_PROPOSAL_INVALID: 'There is no valid price to publish.',
  DP_PRICING_COST_CONTEXT_UNAVAILABLE: 'The cost for this product is unavailable, so the price cannot be checked.',
  DP_PRICE_STAFF_PUBLICATION_DISABLED: 'Price publishing from the Admin is disabled in this environment.',
  DP_PRICE_PUBLICATION_CONFLICT: 'The store could not apply this price. Reload the queue and check the product.',
};

/** The readable reason for a hold or review code; an unknown code shows as it is. */
export const holdReasonLabel = (code: string) =>
  Object.prototype.hasOwnProperty.call(HOLD_REASONS, code)
    ? HOLD_REASONS[code]
    : Object.prototype.hasOwnProperty.call(DP_PRICE_ERRORS, code)
      ? DP_PRICE_ERRORS[code]
      : code;
/** The current store price; for `target_unavailable` the product is not published, so there is none to show. */
export const dpCurrentPriceText = (row: DpPriceReviewItem) => {
  // A HELD row only has the price Batch (or the store) read; a PROPOSED row has the price customers paid.
  const price = row.previousEffectivePrice ?? row.currentSaleSgd ?? row.currentRegularSgd;
  if (price != null) {
    return `${price} SGD`;
  }
  return row.holdReason === 'target_unavailable' ? 'Product unavailable' : 'Unknown';
};
export const dpProposedPrice = (row: DpPriceReviewItem) => row.proposedSalePrice ?? row.proposedRegularPrice;

export function safeProductUrl(value: string | null, host: string | undefined): string | undefined {
  if (!value || !host) {
    return undefined;
  }
  try {
    const url = new URL(value);
    return url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      (url.hostname === host || url.hostname.endsWith(`.${host}`))
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
}
